import { useState, useEffect, useCallback, useRef } from "react";
import { notificationService } from "@root/services/api/notificationService";
import { NotificationDto, GetNotificationsResponse } from "@root/utils/dtos";
import { hasErrors } from "@root/utils/functions";

export const useNotifications = (pollingInterval: number = 30000) => {
  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
  });
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load notifications (includes unreadCount)
  const loadNotifications = useCallback(
    async (page: number = 1, pageSize: number = 20) => {
      try {
        setLoading(true);
        const response = await notificationService.getNotifications(page, pageSize);
        if (response?.data && !hasErrors(response)) {
          const data = response.data as GetNotificationsResponse;
          setNotifications(data.notifications);
          setUnreadCount(data.unreadCount);
          setPagination({
            page: data.page,
            pageSize: data.pageSize,
            totalCount: data.totalCount,
            totalPages: data.totalPages,
          });
        }
      } catch (err: any) {
        console.error("Error loading notifications:", err);
        setError(err.response?.data || "Failed to load notifications");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Mark as read
  const markAsRead = useCallback(
    async (notificationId: string) => {
      try {
        const response = await notificationService.markAsRead(notificationId);
        if (response.isSuccess && (response.statusCode === 204 || response.statusCode === 200)) {
          setNotifications((prev) =>
            prev.map((n) =>
              n.id === notificationId
                ? { ...n, isRead: true, readAt: new Date().toISOString() }
                : n
            )
          );
          setUnreadCount((prev) => Math.max(0, prev - 1));
          await loadNotifications(pagination.page, pagination.pageSize);
        } else {
          if (response.statusCode === 404) {
            setError("Notification not found");
          } else if (response.statusCode === 401) {
            setError("Unauthorized - please login again");
          } else {
            setError(response.message || "Failed to mark notification as read");
          }
        }
      } catch (err: any) {
        console.error("Error marking notification as read:", err);
        setError(err.response?.data || "Failed to mark notification as read");
      }
    },
    [loadNotifications, pagination.page, pagination.pageSize]
  );

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      const response = await notificationService.markAllAsRead();
      if (response.isSuccess && (response.statusCode === 204 || response.statusCode === 200)) {
        setNotifications((prev) =>
          prev.map((n) => ({ ...n, isRead: true, readAt: new Date().toISOString() }))
        );
        setUnreadCount(0);
        await loadNotifications(pagination.page, pagination.pageSize);
      } else {
        if (response.statusCode === 401) {
          setError("Unauthorized - please login again");
        } else {
          setError(response.message || "Failed to mark all notifications as read");
        }
      }
    } catch (err: any) {
      console.error("Error marking all notifications as read:", err);
      setError(err.response?.data || "Failed to mark all notifications as read");
    }
  }, [loadNotifications, pagination.page, pagination.pageSize]);

  // Refresh
  const refresh = useCallback(() => {
    loadNotifications(pagination.page, pagination.pageSize);
  }, [loadNotifications, pagination.page, pagination.pageSize]);

  // Initial load
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Polling — single call covers badge + list
  useEffect(() => {
    if (pollingInterval > 0) {
      intervalRef.current = setInterval(() => {
        // Page 1 always refreshes via loadNotifications; page 2+ also refreshes current page
        loadNotifications(pagination.page > 1 ? pagination.page : 1, pagination.pageSize);
      }, pollingInterval);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [pollingInterval, loadNotifications, pagination.page, pagination.pageSize]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    pagination,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    refresh,
  };
};
